import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import introJs from "intro.js";
import "intro.js/minified/introjs.min.css";
import {
  getGoogleSyncStatus,
  isSyncContactSkip,
  listContacts,
} from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
import { markSetupStep, fetchSetupScore } from "../../lib/setupScore";
import { isAddonFlowActive, advanceFromKey } from "../../lib/addonIntroFlow";
import ContactsPage from "../ContactsPage/ContactsPage";
import styles from "./ContactSyncStep.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";

// Read agent / business name from the same sessionStorage cache the
// rest of onboarding writes to. Soft defaults so a missing piece
// never blanks the spec header.
const readAgentCtx = () => {
  try {
    const raw = sessionStorage.getItem("agentDetails");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// Race-condition guard for the tour. Returns true if any modal /
// bottom-sheet / dialog is currently visible — we don't want intro.js
// laying its overlay on top of an already-open popup. Detects:
//   - NewModal's bottom-sheet overlay (`bottommodaloverlay` class)
//   - SyncDialog's backdrop (`backdrop` class on the ContactManager
//     module's stylesheet)
//   - Any element with role="dialog" + aria-modal="true"
//   - Body locked by NewModal (overflow:hidden + position:fixed)
const isAnyPopupOpen = () => {
  try {
    const overlaySelectors = [
      '[class*="bottommodaloverlay"]',
      '[class*="backdrop"]',
      '[role="dialog"][aria-modal="true"]',
      '[data-sync-dialog-open="true"]',
    ];
    for (const sel of overlaySelectors) {
      const el = document.querySelector(sel);
      if (!el) continue;
      // offsetParent is null for position:fixed elements (like SyncDialog's
      // backdrop). Use getBoundingClientRect as a robust visibility test —
      // a zero-size rect means the element is hidden / display:none.
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return true;
    }
    // NewModal locks the body via inline style — useful tertiary
    // signal in case the overlay class name changes someday.
    if (
      document.body.style.position === "fixed" &&
      document.body.style.overflow === "hidden"
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// Returns true only when every tab the tour will highlight is
// actually rendered AND visible in the DOM. Without this, a slow
// listContacts response leaves ContactsPage in its loading skeleton
// state — tab buttons aren't mounted yet, intro.js can't find its
// targets, and the highlight box ends up framing empty white space
// (or a wrong element). We poll for presence before starting.
/*
 * Are the tour's anchor elements actually LAID OUT yet?
 *
 * This used to test `el.offsetParent !== null`, which only proves the element
 * is attached and not display:none — it says nothing about SIZE. intro.js
 * positions a tooltip from getBoundingClientRect(); when that rect is zero it
 * falls back to a floating tooltip in the top-left corner of the viewport.
 * That is the "tour box appears top-left instead of on the tabs" bug.
 *
 * It showed up specifically right after a first sync because that path starts
 * the tour after only 250ms (vs 1500ms elsewhere) while ContactManager is
 * still re-rendering with the newly synced rows — attached, but not yet sized.
 *
 * Testing the rect makes the gate check the SAME property intro.js positions
 * from, so the existing 20 x 500ms retry loop now genuinely waits for layout
 * instead of passing early. Same test `isAnyPopupOpen` above already uses, and
 * for the same documented reason.
 */
const areTourTargetsPresent = (selectors) => {
  try {
    return selectors.every((sel) => {
      const el = document.querySelector(sel);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  } catch {
    return false;
  }
};

/*
 * ContactSyncStep — Screen 14 wrapper around the dashboard
 * `ContactsPage`.
 *
 * Layout (top → bottom):
 *   1. Spec-copy header — Sophia avatar + "Help Sophia Recognise Your
 *      People" + body + privacy chip. Replaces ContactsPage's purple
 *      "Contacts" header (we pass hideHeader=true).
 *   2. ContactsPage — same data + handlers + tabs + list + sheets the
 *      dashboard uses at /contacts.
 *   3. Fixed-bottom rail — purple Continue + underlined Skip.
 *
 * Behaviour:
 *   - On mount, scope-check via /calender-check.
 *       Scope MISSING  → autoOpenSync=true so SyncDialog pops on first
 *                       paint. After SyncDialog closes → re-check
 *                       scope, fire markSetupStep("contactsSynced")
 *                       if granted, then start the tour.
 *       Scope PRESENT  → skip the SyncDialog entirely (user already
 *                       synced earlier in onboarding), start the tour
 *                       after a 1.5s settle delay so ContactsPage's
 *                       loading skeleton has cleared.
 *
 *   - Google OAuth redirect: when SyncDialog kicks OAuth, the backend
 *     callback lands on /contacts?google_sync=success. We set a
 *     localStorage flag on mount; ContactsPage's google_sync handler
 *     reads it and bounces back to /contact-sync — same one-shot
 *     pattern PostPurchase uses for /connect's returnTo.
 *
 *   - Tour: 3 steps over the three tabs. onbeforechange clicks the
 *     matching tab so the user SEES the tab content, not just the
 *     button. Locks viewport scroll + disables interaction with
 *     highlighted elements (matches FastAgentDetails' intro.js
 *     recipe). User advances only via Next/Done.
 *
 *   - Skip / Continue → /go-live-celebration. Skip never fires the
 *     setup score; Continue lets any earlier credit stand.
 */

// Removed CONTACTS_SCOPE — we now use getGoogleSyncStatus({ hasToken })
// instead of calenderCheck(scope) because the Passport Google strategy
// does not persist the scope string to GoogleCalendarToken.
/*
 * Default next-route after Skip / Continue. Both onboarding flows
 * (GoLive + PostPurchase) now land on /fast-agent-detail directly,
 * which mounts a one-shot "Setup Summary" dialog (gated by the same
 * flag that gates the dashboard intro tour) instead of routing
 * through the older /go-live-celebration screen.
 *
 * Callers can still pass nextRoute="..." to override per-host, but
 * the default is now the dashboard. The summary dialog handles
 * confetti + tour kickoff so we don't need a separate celebration.
 */
const DEFAULT_NEXT_ROUTE = "/fast-agent-detail";
const OAUTH_BOUNCE_FLAG = "contactSync.expectGoogleSyncReturn";

/*
 * Session flag read by FastAgentDetails on mount. When present, that
 * page opens the "Setup Summary" dialog (with confetti) one time and
 * then defers to the existing dashboard intro tour. We set this from
 * BOTH the Skip and Continue branches because either path means the
 * user just finished onboarding and lands on the dashboard for the
 * first time.
 */
const SETUP_SUMMARY_FLAG = "fastAgentDetail.showSetupSummary";

// Build the 3-step tour with the agent's actual name interpolated
// into the copy. Must be a factory (not a module-level constant) so
// re-mounts pick up a renamed agent. Markup is intro.js's lightweight
// HTML — `<b>` is the only tag we need.
const buildTourSteps = (agentName) => [
  {
    element: '[data-tour="my-contacts-tab"]',
    // `title` intro.js ke header me dikhta hai — bina iske header khaali
    // reh jata tha aur upar faltu gap dikhta tha.
    title: "My Contacts",
    intro: `Your VIP list. ${agentName} greets these callers by name.`,
    position: "bottom",
  },
  {
    element: '[data-tour="agent-contacts-tab"]',
    title: "Agent Contacts",
    intro:
      "Auto-saved from real calls. Promote frequent callers to your contacts in one tap.",
    position: "bottom",
  },
  {
    element: '[data-tour="blacklist-tab"]',
    title: "Blacklist",
    intro:
      "Block spam callers. Inside you'll find <b>Auto Spam Blocking</b> and per-agent spam settings.",
    position: "bottom",
  },
];

const TAB_SELECTORS = [
  '[data-tour="my-contacts-tab"]',
  '[data-tour="agent-contacts-tab"]',
  '[data-tour="blacklist-tab"]',
];

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect
      x="5"
      y="11"
      width="14"
      height="9"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 11V7a4 4 0 0 1 8 0v4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

export default function ContactSyncStep({ nextRoute = DEFAULT_NEXT_ROUTE } = {}) {
  const navigate = useNavigate();
  // Single source of truth for the post-step navigation target.
  // Prop takes precedence over the module default so each host
  // route can pick the right downstream surface.
  const NEXT_ROUTE = nextRoute || DEFAULT_NEXT_ROUTE;

  // ── Agent / business context for the spec header. Read once at
  // mount; sessionStorage is stable through the onboarding session,
  // and re-reading on every render would just churn refs. ──
  const ctx = readAgentCtx();
  const agentName = ctx?.agent?.agentName || "Sophia";
  const agentId = ctx?.agent?.agent_id || ctx?.agentId || null;

  // True once we've finished the initial pre-flight (scope check +
  // user-details check). Until then we don't render ContactsPage —
  // otherwise we'd flash either an unwanted SyncDialog (if user
  // already synced) or a no-op mount that has to be torn down.
  const [scopeChecked, setScopeChecked] = useState(false);
  const [autoOpenSync, setAutoOpenSync] = useState(false);

  const tourTriggeredRef = useRef(false);
  const scoreFiredRef = useRef(false);
  const advancedRef = useRef(false);
  // Hold a handle on the running tour so we can exit() on
  // skip/continue and not leak the overlay onto the next route.
  const tourRef = useRef(null);
  // Scroll-lock listeners attached during the tour. Stored so
  // cleanup can detach the exact same function refs.
  const scrollLockRef = useRef(null);
  // Retry counter for the popup-open race-condition guard. Cap
  // retries so we never block the tour forever if something is
  // genuinely stuck open.
  const tourRetryCountRef = useRef(0);

  // ── OAuth bounce-back flag ───────────────────────────────────
  // Set on mount; cleared ONLY by:
  //   - ContactsPage's google_sync handler when it bounces back
  //     (consumed → cleared)
  //   - Our own goToCelebration / handleSkip (user is leaving
  //     /contact-sync without going through OAuth)
  //
  // Critically, we do NOT clear it in this useEffect's cleanup
  // function. The OAuth redirect uses `window.location.href = authUrl`
  // which triggers React effect cleanups before the page unloads —
  // earlier versions cleared the flag on cleanup, which meant the
  // flag was already gone by the time Google's callback bounced the
  // user back to /contacts. Result: bounce-back logic never fired
  // and the user ended up stranded on /contacts (or /dashboard via
  // the SecureRoute fallback) instead of returning to /contact-sync.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem(OAUTH_BOUNCE_FLAG, "true");
    // Intentionally no cleanup — see comment above.
  }, []);

  // Final-mile cleanup: if the user navigates away (skip / continue
  // / browser back) while the tour is still running, exit it so the
  // overlay doesn't bleed into the next route.
  useEffect(() => {
    return () => {
      try {
        tourRef.current?.exit?.(true);
      } catch (_e) {
        /* best-effort */
      }
      // Defensive: if cleanup fired mid-tour, restore body
      // scroll state in case onexit didn't run.
      document.body.classList.remove(styles.tourOpen);
      if (scrollLockRef.current) {
        window.removeEventListener("wheel", scrollLockRef.current);
        window.removeEventListener("touchmove", scrollLockRef.current);
        scrollLockRef.current = null;
      }
    };
  }, []);

  // ── Mount-time pre-flight ────────────────────────────────────
  //
  // The literal "does the user have synced contacts?" check is a
  // contact count via /contact-routes/contacts. This is the single
  // source of truth — `isSyncContactSkip` and the Google scope are
  // both proxies that can lie:
  //
  //   - `isSyncContactSkip = true` may have been set by a previous
  //     Skip click without any actual sync (zero contacts).
  //   - `contacts.readonly` scope may be granted but the sync API
  //     failed silently (zero contacts).
  //
  // So we just count. If the user has contacts → skip the dialog
  // and run the tour. If 0 → pop SyncDialog so they can sync now.
  //
  // Special case: returning from OAuth (?google_sync=success). The
  // sync is in flight (ContactsPage's google_sync handler triggers
  // it on mount) — count would race the in-flight POST. So we just
  // skip the dialog and let the tour fire after settle. If the
  // sync actually fails, the user can re-open SyncDialog via the
  // dashboard /contacts FAB later.
  //
  // We still scope-check in parallel so we can credit
  // `contactsSynced` (10 pts) when the user already authorised
  // contacts earlier — idempotent server-side either way.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const userId = decodeToken(token)?.id;
        if (!userId) {
          // Without a session we can't check anything; safest is to
          // pop the SyncDialog so the user has an action.
          setAutoOpenSync(true);
          setScopeChecked(true);
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const returningFromOAuth = params.get("google_sync") === "success";

        // Parallel: count contacts + check Google token presence.
        // listContacts limit:1 → cheap "is the table empty?" probe.
        // getGoogleSyncStatus returns { hasToken } — reliable signal
        // that the user connected Google (unlike calenderCheck's scope
        // which the Passport strategy never persists).
        const [contactsRes, syncStatusRes, scoreRes] = await Promise.all([
          listContacts({ page: 1, limit: 1 }).catch(() => null),
          getGoogleSyncStatus().catch(() => null),
          fetchSetupScore(agentId).catch(() => null),
        ]);

        // Normalise the count — endpoint shape has drifted across
        // versions (we've seen `total`, nested `.data.total`, and
        // bare `.contacts.length`). Fall back to array length.
        const total =
          contactsRes?.total ??
          contactsRes?.data?.total ??
          contactsRes?.contacts?.length ??
          contactsRes?.data?.length ??
          0;
        const userHasContacts = Number(total) > 0;

        const hasGoogleToken = syncStatusRes?.hasToken === true;
        const scoreSynced = scoreRes?.steps?.contactsSynced?.done === true;

        /*
         * ── "Contacts already handled" ──────────────────────────────
         *
         * Deliberately NOT `EndUsers.isSyncContactSkip`. That column is written
         * by THREE different events — a contacts grant at login
         * (googleAuth.js:2892), a successful sync (:3185), AND a plain Skip tap
         * (endUserController.js:3572, via PUT /updateSyncContactSkip). It means
         * "the user dealt with this", so reading it would make one Skip
         * permanent.
         *
         * And deliberately NOT `hasGoogleToken` either. I originally included
         * it, believing it was strictly contacts-scoped. It is not: the check
         * (Contactcontroller.js:721) accepts an EMPTY scope string as
         * "has contacts" for legacy back-compat —
         *   scopeStr === "" || scopeStr.includes("contacts.readonly")
         * — and the CALENDAR OAuth writes `scope: tokens.scope || null`
         * (googleAuth.js:688). A null scope reads back as "", so merely
         * connecting the calendar flipped hasToken true and this step skipped
         * itself for someone who had never synced a contact.
         *
         * And — correcting the previous version of this note — deliberately NOT
         * contact ROWS either. Rows were described here as "the only signal a
         * calendar connect cannot fabricate". That is false. The calendar-only
         * OAuth callback runs a BONUS contacts import (googleAuth.js:698-706)
         * gated on `tokens.scope.includes("contacts.readonly")`, and because
         * connectCalendarOnly sets `include_granted_scopes: true`
         * (googleAuth.js:559) Google echoes back any contacts scope the account
         * granted at ANY point in the past. For such a user — the norm on the
         * native app, where Google sign-in requests contacts (googleAuth.js:179)
         * — connecting the calendar silently bulk-inserts their whole Google
         * address book, so rows exist for someone who never touched contacts.
         *
         * What is left is the setup-score `contactsSynced` mark, written only by
         * a deliberate user action: a Google sync, a CSV import, the phone
         * picker or a manual add (ContactsPage), and GoogleAuthStep only when
         * `contacts.readonly` is actually in the granted scope set.
         *
         * `userHasContacts` stays live below — it still (correctly) decides
         * whether to POP THE SYNC DIALOG. Having contacts already is a fine
         * reason not to re-prompt for a sync; it is not a reason to skip the
         * step entirely.
         *
         * Accepted cost: a user who synced BEFORE the setup score existed (rows,
         * no mark) is asked once more; acting on the screen credits the step.
         * Asking again is the safe failure; silently skipping a step they never
         * completed is not.
         *
         * A Skip tap sets no mark either, so a skipper is asked again too.
         */
        const contactsHandled = scoreSynced;

        /*
         * ── Already-done self-skip ──────────────────────────────────
         *
         * The add-on sequence is a STATIC list: TriageStep always passes
         * ["contact","calendar"] and startAddonFlow stores it verbatim —
         * nothing ever filtered it against feature state. So a second pass
         * through triage replayed contact sync even when it was already done.
         *
         * Fixed here rather than by filtering the array at build time, so it
         * holds no matter HOW the screen was reached (sequence, direct URL,
         * back-nav, a future caller) and each screen judges its own feature
         * with its own source of truth.
         *
         * Two hard conditions:
         *   - `isAddonFlowActive()` — outside a sequence there is nothing to
         *     advance TO. advanceFromKey() would clear the flow, arm the
         *     dashboard summary and bounce the user out, so a direct visit to
         *     /contact-sync must still render normally.
         *   - not returningFromOAuth — that path has its own handling below
         *     (sync in flight, score credit, tour). Left exactly as it was.
         *
         * `replace: true` so Back cannot land back on the screen we skipped.
         */
        if (contactsHandled && !returningFromOAuth && isAddonFlowActive()) {
          navigate(advanceFromKey("contact"), { replace: true });
          return;
        }

        const skipDialog = userHasContacts || scoreSynced || returningFromOAuth;

        if (skipDialog) {
          setAutoOpenSync(false);
          setScopeChecked(true);
          // Credit setup score if Google token exists (regardless of
          // whether OAuth just happened or was granted earlier).
          // Idempotent server-side, so no harm if already credited.
          if (hasGoogleToken && !scoreFiredRef.current) {
            scoreFiredRef.current = true;
            markSetupStep("contactsSynced");
          }
          // FastAgentDetails waits 1500ms to let DOM elements render
          // before intro.start() — same wait here.
          setTimeout(() => {
            if (!tourTriggeredRef.current) {
              tourTriggeredRef.current = true;
              startTour();
            }
          }, 1500);
        } else {
          /*
           * Zero contacts and not just-synced — pop SyncDialog so the user has
           * a clear next action.
           *
           * Suppressed once contacts are handled. Deliberately the same
           * `contactsHandled` the self-skip above uses so the two decisions
           * cannot drift apart — reaching this branch already implies it is
           * false (skipDialog subsumes it), so this reads as `true` today and
           * stays correct if either definition later changes.
           *
           * The sheet stays reachable on demand either way: the FAB "+" → Sync
           * entry in ContactManager is gated only on `canSync`.
           */
          setAutoOpenSync(!contactsHandled);
          setScopeChecked(true);
        }
      } catch (err) {
        console.error("[ContactSyncStep] pre-flight failed:", err);
        // Fail-open: show the SyncDialog so the user has an action.
        setAutoOpenSync(true);
        setScopeChecked(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Tour bootstrap. Mirrors FastAgentDetails' recipe exactly:
  //   - introJs() constructor (legacy API still works in v8 with a
  //     deprecation warning)
  //   - exitOnOverlayClick: false  (no accidental dismissal)
  //   - disableInteraction: true   (only Next/Done advances)
  //   - scrollToElement: false     (we scroll manually so the
  //     highlight box doesn't fight intro.js's auto-scroll)
  //   - wheel + touchmove listeners pinned during the tour
  //   - body class for any tour-open CSS overrides
  // ────────────────────────────────────────────────────────────────
  const startTour = () => {
    if (typeof introJs !== "function") return;

    // Race-condition guard. Two reasons we'd defer:
    //   (a) A modal / sheet / dialog is still visible — running the
    //       tour over it stacks intro.js's overlay on top of an
    //       existing one, looks like a glitch.
    //   (b) listContacts is still in flight, ContactsPage is showing
    //       its loading skeleton, and the tab buttons aren't in the
    //       DOM yet — intro.js can't find its targets and ends up
    //       framing empty white space (or the wrong element if it
    //       falls back to body).
    // Defer 500ms and re-check, cap retries at 20 (10s total) so a
    // genuinely stuck state can't freeze the tour forever — after
    // that, give up silently and let the user advance via
    // Skip / Continue.
    const popupOpen = isAnyPopupOpen();
    const targetsReady = areTourTargetsPresent(TAB_SELECTORS);
    if (popupOpen || !targetsReady) {
      if (tourRetryCountRef.current >= 20) {
        console.warn(
          "[ContactSyncStep] tour deferred — " +
            (popupOpen ? "popup still open" : "tab targets not in DOM") +
            " after 20 retries; giving up",
        );
        return;
      }
      tourRetryCountRef.current += 1;
      setTimeout(startTour, 500);
      return;
    }

    const intro = introJs();
    tourRef.current = intro;

    intro.setOptions({
      steps: buildTourSteps(agentName),
      showProgress: true,
      showButtons: true,
      showBullets: false,
      exitOnOverlayClick: false,
      exitOnEsc: false,
      disableInteraction: true,
      scrollToElement: false,
      fixElementPosition: true,
      overlayOpacity: 0.45,
      nextLabel: "Next",
      prevLabel: "Back",
      doneLabel: "Got it",
      tooltipClass: styles.tourTooltip,
      highlightClass: styles.tourHighlight,
    });

    const lockScroll = (e) => {
      if (e.cancelable) e.preventDefault();
    };
    scrollLockRef.current = lockScroll;

    const cleanup = () => {
      window.removeEventListener("wheel", lockScroll);
      window.removeEventListener("touchmove", lockScroll);
      document.body.classList.remove(styles.tourOpen);
      tourRef.current = null;
      scrollLockRef.current = null;
    };

    intro.onstart(() => {
      window.addEventListener("wheel", lockScroll, { passive: false });
      window.addEventListener("touchmove", lockScroll, { passive: false });
      document.body.classList.add(styles.tourOpen);
    });

    // Switch the actual tab BEFORE intro.js positions the highlight,
    // so the visible content matches what the tooltip describes.
    intro.onbeforechange(function () {
      try {
        const idx = this._currentStep;
        const sel = TAB_SELECTORS[idx];
        if (!sel) return;
        const tabBtn = document.querySelector(sel);
        if (tabBtn && typeof tabBtn.click === "function") {
          tabBtn.click();
        }
      } catch (e) {
        console.warn("[ContactSyncStep] tab switch failed:", e);
      }
    });

    // Manual scroll into view + refresh, mirroring FastAgentDetails
    // — gives more precise positioning than intro.js's auto-scroll.
    intro.onafterchange((targetElement) => {
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        setTimeout(() => {
          try {
            intro.refresh();
          } catch (_e) {
            /* refresh can throw if tour already exited */
          }
        }, 300);
      }
    });

    intro.oncomplete(cleanup);
    intro.onexit(cleanup);

    intro.start();
    // Force an immediate re-measure. intro.js sometimes misreads the
    // first element's bounding rect when position:sticky or layout
    // shifts from tab-click re-renders are in play. This 50ms nudge
    // fires before the 300ms one below, fixing the "entire bar"
    // highlight glitch on the first step.
    setTimeout(() => {
      try { intro.refresh(); } catch (_e) { /* best-effort */ }
    }, 50);
    setTimeout(() => {
      try {
        intro.refresh();
      } catch (_e) {
        /* best-effort */
      }
    }, 300);
  };

  // ContactManager fires this when SyncDialog closes (regardless of
  // whether the user actually synced). We use it to: (a) credit
  // setup score if scope was granted during the dialog flow,
  // (b) start the tour. Guarded so re-opens of the dialog don't
  // restart the tour.
  const handleSyncDialogClose = async () => {
    try {
      if (!scoreFiredRef.current) {
        const syncStatus = await getGoogleSyncStatus().catch(() => null);
        if (syncStatus?.hasToken) {
          scoreFiredRef.current = true;
          markSetupStep("contactsSynced");
        }
      }
    } catch (err) {
      console.warn("[ContactSyncStep] Google token re-check failed:", err);
    }

    if (tourTriggeredRef.current) return;
    tourTriggeredRef.current = true;
    // 250ms breather lets the bottom-sheet's exit animation complete
    // before the intro.js overlay paints.
    setTimeout(startTour, 250);
  };

  const goToCelebration = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    // Clear the OAuth bounce-back flag so a later dashboard
    // visit to /contacts doesn't accidentally redirect back
    // to /contact-sync — we're done with this screen.
    localStorage.removeItem(OAUTH_BOUNCE_FLAG);

    /*
     * Add-on intro sequence: when the user reached /contact-sync via the
     * "Sophia is live" dialog's Contact intro (Allow → /contact-sync) and
     * another screen (calendar) is still queued, continue the sequence
     * instead of finishing to the dashboard. When the queue is empty,
     * advanceAddonFlow itself arms the Setup Summary + returns the
     * dashboard route, so we fall through to the normal path below for
     * that case (isAddonFlowActive is false once the last screen is done).
     */
    if (isAddonFlowActive()) {
      try {
        tourRef.current?.exit?.(true);
      } catch (_e) {
        /* best-effort */
      }
      // /contact-sync represents the "contact" position in the sequence.
      navigate(advanceFromKey("contact"), { replace: true });
      return;
    }
    /*
     * Signal to FastAgentDetails that the user just finished onboarding
     * so it should open the one-shot Setup Summary dialog (with confetti)
     * before kicking off the dashboard intro tour. Set whenever the
     * destination is the dashboard — covers both the default route and
     * an explicit `nextRoute="/fast-agent-detail"` from PostPurchase.
     * Other destinations (custom hosts) skip the summary entirely.
     */
    if (NEXT_ROUTE === "/fast-agent-detail") {
      try {
        sessionStorage.setItem(SETUP_SUMMARY_FLAG, "true");
      } catch (_e) {
        /* sessionStorage disabled — summary just won't show, harmless. */
      }
    }
    try {
      tourRef.current?.exit?.(true);
    } catch (_e) {
      /* best-effort */
    }
    navigate(NEXT_ROUTE, { replace: true });
  };

  const handleSkip = () => {
    // Persist the "user dealt with sync" flag so future visits
    // (whether to /contact-sync or to /contacts on the dashboard)
    // don't re-prompt with the SyncDialog. Fire-and-forget — the
    // navigation should not wait for the PUT to land.
    isSyncContactSkip().catch((e) =>
      console.warn("[ContactSyncStep] skip flag PUT failed:", e),
    );
    // No setup-score fire on skip. If scope was granted earlier in
    // onboarding, that credit already stands.
    goToCelebration();
  };

  return (
    <div className={styles.page}>
      {/* ── Spec header (Screen 14 copy) ───────────────────────── */}
      <div className={styles.specHeader}>
        {/*
           Heading per Figma — TWO lines:
             1. "Help {agentName}"   (light, 32px)
             2. "Recognize callers"  (bold, 32px)

           Both lines use the same size so the agent name doesn't
           visually outweigh the action verb. Matches the hero
           treatment used on Step 1 (Assign Number) and Triage so
           the wizard family reads consistently.
        */}
        <h1 className={styles.headlineLight}>Help {agentName}</h1>
        <h1 className={styles.headlineBold}>Recognize callers</h1>

        <p className={styles.body}>
          {agentName} greets your contacts by name and carefully screens
          unknown callers.
        </p>

        {/*
           Privacy reassurance — orange/coral treatment per Figma.
           Stronger than the older lavender chip; the bold lead-in
           emphasises the limited data scope ("only read names and
           numbers") so the user feels okay granting Google Contacts.
        */}
        {/* <div className={styles.privacyChip}>
          <span className={styles.privacyIcon} aria-hidden="true">
            <LockIcon />
          </span>
          <span className={styles.privacyText}>
            <b className={styles.privacyTextBold}>
              We only read names and numbers.
            </b>
            Nothing is stored, shared, or used for marketing
          </span>
        </div> */}
      </div>

      {/* ── ContactsPage (mounted only after scope check resolves) ─
          Same component the dashboard uses. Hide its purple header,
          delegate auto-open of SyncDialog based on the scope check. */}
      <div className={styles.contactsHost}>
        {scopeChecked && (
          <ContactsPage
            autoOpenSync={autoOpenSync}
            onSyncDialogClose={handleSyncDialogClose}
            hideHeader={true}
            oauthReturnTo="/contact-sync"
          />
        )}
      </div>

      {/* ── Fixed-bottom safety rail ───────────────────────────── */}
      <div className={styles.bottomBar}>
        <AnimatedButton
          label={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              Complete Setup
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M7.33944 13.093L5.94953 11.7166L10.1125 7.55362H-0.00146484V5.54297H10.1125L5.94953 1.38672L7.33944 0.00355083L13.8842 6.5483L7.33944 13.093Z" fill="white" />
              </svg>
            </span>
          }
          onClick={goToCelebration}
          positionType="relative"
          width="100%"
          bottom="0px"
        />
        {/* <button
          type="button"
          className={styles.skipLink}
          onClick={handleSkip}
        >
          Skip — I'll do this later
        </button> */}
      </div>
    </div>
  );
}
